import {
  companionControllerExchangePairingToken,
  companionControllerRefreshToken,
  isRetriedRequest,
  retryRequest,
  useAcquisitionStoreServer,
  type ResponseCompanionAuthDto,
} from '@/api';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { usePiniaRefStore } from '../../composables/usePiniaRefStore';

let isRefreshingToken = ref<Promise<void> | null>(null);
const isBootstrapped = ref(false);

export const useCompanionAuthStore = defineStore(
  'companion-auth',
  () => {
    const router = useRouter();
    const { setHeaders, addResponseInterceptor } = useAcquisitionStoreServer();

    const companionId = ref('');
    const accessToken = ref('');
    const refreshToken = ref('');
    const expiresAt = ref<Date | null>(null);
    const refreshExpiresAt = ref<Date | null>(null);

    const isAuthenticated = computed(() => !!accessToken.value && !!companionId.value);

    const setTokens = (data: ResponseCompanionAuthDto) => {
      companionId.value = data.id;
      accessToken.value = data.accessToken;
      refreshToken.value = data.refreshToken;
      expiresAt.value = new Date(data.expiresAt);
      refreshExpiresAt.value = new Date(data.refreshTokenExpiresAt);
      setHeaders({ Authorization: `Bearer ${data.accessToken}` });
    };

    const clearSession = () => {
      companionId.value = '';
      accessToken.value = '';
      refreshToken.value = '';
      expiresAt.value = null;
      refreshExpiresAt.value = null;
    };

    const pairSession = async (pairingToken: string): Promise<string | null> => {
      const { status, data } = await companionControllerExchangePairingToken({
        headers: { Authorization: `Bearer ${pairingToken}` },
      });

      if (status !== 200) {
        clearSession();
        return null;
      }

      setTokens(data);
      return data.id;
    };

    const refreshSession = (): Promise<void> => {
      if (isRefreshingToken.value) return isRefreshingToken.value;
      if (!refreshToken.value)
        return Promise.reject(new Error('No companion refresh token available'));

      isRefreshingToken.value = companionControllerRefreshToken({
        headers: { Authorization: `Bearer ${refreshToken.value}` },
      })
        .then(({ status, data }) => {
          if (status !== 200) throw new Error('Failed to refresh companion session');
          setTokens(data);
        })
        .catch((error) => {
          clearSession();
          throw error;
        })
        .finally(() => {
          isRefreshingToken.value = null;
        });

      return isRefreshingToken.value;
    };

    const bootstrap = () => {
      if (isBootstrapped.value) return;

      addResponseInterceptor(async (url, request, response) => {
        const canRefresh =
          isAuthenticated.value &&
          !!refreshToken.value &&
          (!refreshExpiresAt.value || new Date() < new Date(refreshExpiresAt.value));

        switch (response.status) {
          case 401:
            // `refresh-token` rather than `companion/refresh-token`: a 401 on the
            // refresh call itself must fail, not wait on its own refresh.
            if (
              canRefresh &&
              !isRetriedRequest(request) &&
              !url.endsWith('refresh-token') &&
              !url.endsWith('/push-notification/unsubscribe')
            ) {
              // A failed refresh has already cleared the session - the caller
              // gets its original 401 instead of a rejection nobody handles
              try {
                await refreshSession();
              } catch {
                return response;
              }

              return retryRequest(url, request);
            }
            break;
          case 403:
            router.replace({ name: 'forbidden' });
            break;
        }
        return response;
      });

      isBootstrapped.value = true;
    };

    // Everything the refresh needs is returned: a setup store only persists
    // what it exposes, and a reload would otherwise leave nothing to refresh with.
    return {
      companionId,
      accessToken,
      refreshToken,
      expiresAt,
      refreshExpiresAt,
      isAuthenticated,
      pairSession,
      refreshSession,
      clearSession,
      bootstrap,
    };
  },
  {
    persist: {
      pick: ['companionId', 'accessToken', 'refreshToken', 'expiresAt', 'refreshExpiresAt'],
      serializer: {
        serialize: JSON.stringify,
        deserialize: (data) => {
          const parsed = JSON.parse(data);
          if (parsed.expiresAt) parsed.expiresAt = new Date(parsed.expiresAt);
          if (parsed.refreshExpiresAt) parsed.refreshExpiresAt = new Date(parsed.refreshExpiresAt);
          return parsed;
        },
      },
      afterHydrate: (context) => {
        const store = context.store as ReturnType<typeof useCompanionAuthStore>;
        const { setHeaders } = useAcquisitionStoreServer();
        if (store.accessToken) setHeaders({ Authorization: `Bearer ${store.accessToken}` });
      },
    },
  },
);

export const useCompanionAuth = () => usePiniaRefStore(useCompanionAuthStore());
